import { useState } from 'react';
import { captureError } from "@/lib/logger";
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Loader2, User, Car, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import SelectDriverStep from './SelectDriverStep';
import SelectVehicleStep from './SelectVehicleStep';

interface Driver {
    id: number;
    first_name: string;
    last_name: string;
    phone: string;
    photo?: string;
}

interface Vehicle {
    id: number;
    make: string;
    model: string;
    registration_number?: string;
}

interface CreateContractWizardProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: (contractId: number) => void;
}

const STEPS = [
    { key: 'driver', label: 'Select Driver', icon: User, description: 'Choose a driver for the contract' },
    { key: 'vehicle', label: 'Select Vehicle', icon: Car, description: 'Assign a vehicle to the driver' },
];

export default function CreateContractWizard({
    open,
    onOpenChange,
    onSuccess,
}: CreateContractWizardProps) {
    const [step, setStep] = useState(0);
    const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
    const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

    // Create minimal DRAFT contract mutation
    const createContractMutation = useMutation({
        mutationFn: async () => {
            if (!selectedDriver || !selectedVehicle) {
                throw new Error('Driver and vehicle are required');
            }

            // Create contract with minimal data (driver + vehicle only)
            const { data: contract, error: contractError } = await supabase
                .from('contracts')
                .insert({
                    driver_id: selectedDriver.id,
                    vehicle_id: selectedVehicle.id,
                })
                .select('id')
                .single();

            if (contractError) throw contractError;

            // Create initial DRAFT status
            const { error: statusError } = await supabase
                .from('contract_updates')
                .insert({
                    contract_id: contract.id,
                    status: 'DRAFT',
                });

            if (statusError) throw statusError;

            return contract.id;
        },
        onSuccess: (contractId) => {
            toast.success('Draft contract created successfully');
            handleClose();
            onSuccess?.(contractId);
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to create contract');
        },
    });

    const canGoNext = () => {
        if (step === 0) return selectedDriver !== null;
        if (step === 1) return selectedVehicle !== null;
        return true;
    };

    const handleNext = () => {
        if (step === 0 && selectedDriver) {
            setStep(1);
        }
    };

    const handleBack = () => {
        if (step > 0) {
            setStep(step - 1);
        }
    };

    const handleStepClick = (index: number) => {
        // Can only go back or to next step if current step is complete
        if (index < step) {
            setStep(index);
        } else if (index === step + 1 && canGoNext()) {
            setStep(index);
        }
    };

    const handleClose = () => {
        setStep(0);
        setSelectedDriver(null);
        setSelectedVehicle(null);
        onOpenChange(false);
    };

    const handleCreateContract = () => {
        createContractMutation.mutate();
    };

    const isCreating = createContractMutation.isPending;

    const renderStepContent = () => {
        switch (step) {
            case 0:
                return (
                    <SelectDriverStep
                        selectedDriver={selectedDriver}
                        onSelect={setSelectedDriver}
                    />
                );
            case 1:
                return (
                    <SelectVehicleStep
                        selectedVehicle={selectedVehicle}
                        onSelect={setSelectedVehicle}
                    />
                );
            default:
                return null;
        }
    };

    // Helper to check if a step is complete
    const isStepComplete = (index: number) => {
        if (index === 0) return !!selectedDriver;
        if (index === 1) return !!selectedVehicle;
        return false;
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
            >
                {/* Left Sidebar - Progress */}
                <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                    {/* Decorative background elements */}
                    <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                    <div className="mb-8 relative z-10">
                        <h2 className="text-2xl font-semibold tracking-tight">New Contract</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Create a new vehicle rental contract.
                        </p>
                    </div>

                    <div className="space-y-1 relative z-10 flex-1">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            const isActive = i === step;
                            const isCompleted = isStepComplete(i);

                            return (
                                <button
                                    key={s.key}
                                    onClick={() => handleStepClick(i)}
                                    className={cn(
                                        "w-full group flex items-center gap-3 p-3 rounded-xl transition-all duration-300 text-left",
                                        isActive ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-background/50",
                                        isCompleted ? "opacity-100" : "opacity-80",
                                    )}
                                    disabled={i > step && !canGoNext()} // Disable future steps if current isn't ready
                                >
                                    <div className="relative">
                                        <div className={cn(
                                            "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                                            isActive ? "bg-primary text-primary-foreground" :
                                                isCompleted ? "bg-green-100 text-green-600/70 dark:bg-green-900/40 dark:text-green-400/70" : "bg-muted text-muted-foreground",
                                        )}>
                                            <Icon className={cn("h-5 w-5", isCompleted && !isActive && "opacity-60")} />
                                        </div>
                                        {isCompleted && (
                                            <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center ring-2 ring-background">
                                                <Check className="h-2.5 w-2.5 text-white" />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <p className={cn(
                                            "font-medium text-sm transition-colors",
                                            isActive ? "text-foreground" : "text-muted-foreground"
                                        )}>
                                            {s.label}
                                        </p>
                                        <p className="text-xs text-muted-foreground/70 hidden lg:block">
                                            {s.description}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <Button
                        variant="ghost"
                        size="sm"
                        className="mt-auto w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                        onClick={handleClose}
                    >
                        <X className="h-4 w-4 mr-2" />
                        Close
                    </Button>
                </div>

                {/* Right Content */}
                <div className="flex-1 flex flex-col h-full relative">
                    {/* Mobile Header */}
                    <div className="md:hidden p-4 border-b flex items-center justify-between">
                        <div>
                            <h2 className="font-semibold">{STEPS[step].label}</h2>
                            <p className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</p>
                        </div>
                        <Button variant="ghost" size="icon" onClick={handleClose}>
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Main Content Area - fixed layout, child handles scrolling */}
                    <div className="flex-1 flex flex-col overflow-hidden p-6 md:p-10">
                        <div className="flex flex-col h-full max-w-2xl mx-auto w-full">
                            {/* Fixed Header */}
                            <div className="flex-none mb-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                                        <p className="text-muted-foreground">{STEPS[step].description}</p>
                                    </div>
                                    {isStepComplete(step) && (
                                        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium dark:bg-green-900/30 dark:text-green-400">
                                            <Check className="h-3.5 w-3.5" />
                                            Complete
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Scrollable Component Content */}
                            <div className="flex-1 min-h-0 overflow-hidden">
                                {renderStepContent()}
                            </div>
                        </div>
                    </div>

                    {/* Sticky Footer */}
                    <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                        <div className="max-w-2xl mx-auto flex items-center justify-between">
                            <Button
                                variant="outline"
                                onClick={handleBack}
                                disabled={step === 0 || isCreating}
                                className="w-24 rounded-full"
                            >
                                <ChevronLeft className="h-4 w-4 mr-1" />
                                Back
                            </Button>

                            {step < STEPS.length - 1 ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={!canGoNext()}
                                    className="w-32 rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleCreateContract}
                                    disabled={!canGoNext() || isCreating}
                                    className="w-32 rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                >
                                    {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                    Create Contract
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
